import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Form, Row, Tab, Tabs } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { CampaignsService } from '../CampaignsService';
import { PlatformService } from '../../platform/PlatformService';
import { Campaign } from '../../../model/campaign/Campaign';
import { PlatformItem } from '../../../model/platform/PlatformItem';
import { ICampaignsGoals } from '../../../model/campaign/ICampaignsGoals';
import { ICampaignsTypes } from '../../../model/campaign/ICampaignType';
import { ICampaignAdvancedCategory, ICampaignAdvancedClient } from '../../../model/campaign/ICampaignAdvanced';
import { UPDATED_CAMPAIGN } from '../../../enum/Messages';
import { ErrorToast, WarningToast } from '../../../utils/toasters';
import { getCorrectEnums } from '../../../utils/Functions';
import { getPlacementLabel } from '../../../utils/helpers/PlatformLabels';
import { getCountryName } from '../../../utils/helpers/CorrectCountryNames';
import ToggleChip from '../../../shared/components/ToggleChip';
import SingleSelectDropdown from '../../../shared/components/SingleSelectDropdown';
import MultiSelectDropdown from '../../../shared/components/MultiSelectDropdown';

type Section = 'general' | 'platforms' | 'creators' | 'audience';
type Values = Record<string, string>;

interface IDraft {
    general: Values;
    creator: Values;
    audience: Values;
    platforms: Record<number, number>;
    countries: string[];
    audienceCountries: string[];
    categoryIds: number[];
    goalId: string;
    creatorTypeId: string;
    isBarter: boolean;
    isReviewed: boolean;
    canSkipReporting: boolean;
}

type Field = [string, string];

const GENERAL_TEXT: [string, string, boolean][] = [['title', 'Title', false]];
const GENERAL_DATE_KEYS = ['publishFrom', 'publishTo', 'postingDeadline'];
const DURATIONS = Array.from({ length: 12 }, (_, index) => ({
    value: `month_${index + 1}`,
    label: index === 0 ? '1 month' : `${index + 1} months`,
}));
const PERIODS = ['one_time', 'bi_annual', 'quarterly', 'monthly', 'per_activations'];
const LONG_TERM_DAYS_PER_MONTH = 31;
const CREATOR_NUMBERS: Field[] = [
    ['ageMin', 'Age min'],
    ['ageMax', 'Age max'],
];
const AUDIENCE_NUMBERS: Field[] = [
    ['audienceAgeMin', 'Age min'],
    ['audienceAgeMax', 'Age max'],
];

const toDateInput = (value?: string | null) => (value ? String(value).slice(0, 10) : '');
const toNumberInput = (value?: number | null) => (value === null || value === undefined ? '' : String(value));

const pick = (source: Record<string, any>, keys: string[], convert: (value: any) => string): Values =>
    Object.fromEntries(keys.map(key => [key, convert(source[key])]));
const text = (value: any) => value ?? '';
const date = (value: any) => toDateInput(value);
const num = (value: any) => toNumberInput(value);

const toGeneral = (c: Record<string, any>): Values => ({
    ...pick(c, ['title', 'invoiceDuration', 'invoicePeriod'], text),
    ...pick(c, ['date', 'publishFrom', 'publishTo', 'postingDeadline'], date),
    ...pick(c, ['reviewDays'], num),
});
const toCreator = (c: Record<string, any>): Values => ({
    gender: text(c.gender),
    ...pick(c, ['ageMin', 'ageMax'], num),
});
const toAudience = (c: Record<string, any>): Values => ({
    ...pick(c, ['audienceDesc', 'audienceGender'], text),
    ...pick(c, ['audienceAgeMin', 'audienceAgeMax'], num),
});

const mainPlatformId = (item: PlatformItem) => item.mainPlatform?.id ?? 0;

const asPayload = (values: Values, dates: string[], numbers: string[]) =>
    Object.fromEntries(
        Object.entries(values).map(([key, value]) => {
            if (dates.includes(key)) return [key, value || null];
            if (numbers.includes(key)) return [key, value === '' ? undefined : +value];
            return [key, value === '' ? undefined : value];
        })
    );

const sameList = <T,>(a: T[], b: T[]) => JSON.stringify([...a].sort()) === JSON.stringify([...b].sort());

export function CampaignAdvancedEdit() {
    const { id }: any = useParams();
    const navigate = useNavigate();
    const { country, gender } = useSelector((state: any) => state.enums);

    const [campaign, setCampaign] = useState<Campaign | null>(null);
    const [draft, setDraft] = useState<IDraft | null>(null);
    const [snapshot, setSnapshot] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<Section>('general');
    const [platformItems, setPlatformItems] = useState<PlatformItem[]>([]);
    const [selectedMain, setSelectedMain] = useState<number[] | null>(null);
    const [categories, setCategories] = useState<ICampaignAdvancedCategory[]>([]);
    const [goals, setGoals] = useState<ICampaignsGoals[]>([]);
    const [creatorTypes, setCreatorTypes] = useState<ICampaignsTypes[]>([]);
    const [showErrors, setShowErrors] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    function load() {
        Promise.all([
            CampaignsService.getAdvancedCampaign(id),
            CampaignsService.getAdvancedPlatforms(id),
            CampaignsService.getAdvancedCountries(id),
            CampaignsService.getAdvancedAudienceCountries(id),
            CampaignsService.getAdvancedCategories(id),
        ])
            .then(([loadedCampaign, platforms, countries, audienceCountries, loadedCategories]) => {
                const loaded: IDraft = {
                    general: toGeneral(loadedCampaign),
                    creator: toCreator(loadedCampaign),
                    audience: toAudience(loadedCampaign),
                    platforms: Object.fromEntries(
                        platforms.filter(p => p.socialPlatform).map(p => [p.socialPlatform!.id, p.nbPosts])
                    ),
                    countries,
                    audienceCountries,
                    categoryIds: loadedCategories.filter(c => c.isSelected).map(c => c.id),
                    goalId: String((loadedCampaign as any).campaignGoal?.id ?? ''),
                    creatorTypeId: String((loadedCampaign as any).creatorType?.id ?? ''),
                    isBarter: !!loadedCampaign.isCharity,
                    isReviewed: !!(loadedCampaign as any).isReviewed,
                    canSkipReporting: !!(loadedCampaign as any).canSkipReporting,
                };
                setCampaign(loadedCampaign);
                setCategories(loadedCategories);
                setDraft(loaded);
                setSnapshot(JSON.stringify(loaded));
                setSelectedMain(null);
            })
            .catch(err => ErrorToast(err));
    }

    useEffect(load, [id]);

    useEffect(() => {
        PlatformService.getPlatformItems({ page: 1, perPage: 10000, name: '' })
            .then(response => setPlatformItems(response.data))
            .catch(err => ErrorToast(err));
        CampaignsService.getCampaignsTypes({ page: 1, perPage: 10000 })
            .then(response => setCreatorTypes(response.data))
            .catch(err => ErrorToast(err));
        CampaignsService.getCampaignsGoals({ page: 1, perPage: 10000 })
            .then(response => setGoals(response.data))
            .catch(err => ErrorToast(err));
    }, []);

    const mainPlatforms = useMemo(() => {
        const map = new Map<number, { id: number; name: string; path?: string }>();
        platformItems.forEach(item => {
            const key = mainPlatformId(item);
            if (!map.has(key)) {
                map.set(key, {
                    id: key,
                    name: item.mainPlatform?.name ?? item.platform,
                    path: item.mainPlatform?.path,
                });
            }
        });
        return Array.from(map.values());
    }, [platformItems]);

    useEffect(() => {
        if (selectedMain !== null || !draft || platformItems.length === 0) return;
        const ids = platformItems.filter(item => item.id in draft.platforms).map(mainPlatformId);
        setSelectedMain(Array.from(new Set(ids)));
    }, [selectedMain, draft, platformItems]);

    // Which parts of the draft differ from what was loaded; the content creator and audience tabs each own several parts.
    const changes = useMemo(() => {
        const result = {
            general: false,
            platforms: false,
            creatorValues: false,
            countries: false,
            categories: false,
            audienceValues: false,
            audienceCountries: false,
            goal: false,
            creatorType: false,
            barter: false,
            timelineFlags: false,
        };
        if (!draft || snapshot === null) return result;
        const initial = JSON.parse(snapshot) as IDraft;
        const differs = (a: unknown, b: unknown) => JSON.stringify(a) !== JSON.stringify(b);
        result.general = differs(initial.general, draft.general);
        result.platforms = differs(initial.platforms, draft.platforms);
        result.creatorValues = differs(initial.creator, draft.creator);
        result.countries = !sameList(initial.countries, draft.countries);
        result.categories = !sameList(initial.categoryIds, draft.categoryIds);
        result.audienceValues = differs(initial.audience, draft.audience);
        result.audienceCountries = !sameList(initial.audienceCountries, draft.audienceCountries);
        result.goal = initial.goalId !== draft.goalId;
        result.creatorType = initial.creatorTypeId !== draft.creatorTypeId;
        result.barter = initial.isBarter !== draft.isBarter;
        result.timelineFlags =
            initial.isReviewed !== draft.isReviewed || initial.canSkipReporting !== draft.canSkipReporting;
        return result;
    }, [draft, snapshot]);

    const changedSections: Record<Section, boolean> = {
        general: changes.general || changes.goal || changes.creatorType || changes.barter || changes.timelineFlags,
        platforms: changes.platforms,
        creators: changes.creatorValues || changes.countries || changes.categories,
        audience: changes.audienceValues || changes.audienceCountries,
    };
    const isDirty = Object.values(changedSections).some(Boolean);

    useEffect(() => {
        if (!isDirty) return;
        const warn = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', warn);
        return () => window.removeEventListener('beforeunload', warn);
    }, [isDirty]);

    const errors = useMemo(() => {
        const result: Record<string, string> = {};
        if (!draft) return result;
        const { general: g, creator: c, audience: a } = draft;
        if (!g.title.trim()) result.title = 'Title is required';
        if (campaign?.isLongTerm) {
            if (!g.date) result.date = 'Start date is required';
            if (!g.invoiceDuration) result.invoiceDuration = 'Select the duration';
            if (!g.invoicePeriod) result.invoicePeriod = 'Select the payout method';
        } else {
            if (g.publishFrom && g.publishTo && g.publishFrom > g.publishTo) {
                result.publishTo = 'Publish to must be after publish from';
            }
            if (g.date && g.publishFrom && g.date > g.publishFrom) {
                result.date = 'Application deadline can not be after the publication start';
            }
            if (g.publishTo && g.postingDeadline && g.postingDeadline < g.publishTo) {
                result.postingDeadline = 'Reporting deadline can not be before the publication end';
            }
            const initialReviewDays = snapshot ? JSON.parse(snapshot).general?.reviewDays : g.reviewDays;
            // Legacy campaigns can have approval on without days; only insist when the admin touches it.
            const reviewTouched = changes.timelineFlags || initialReviewDays !== g.reviewDays;
            if (draft.isReviewed && reviewTouched && !(+g.reviewDays >= 1)) {
                result.reviewDays = 'Set the number of days';
            }
        }
        if (c.ageMin && c.ageMax && +c.ageMin > +c.ageMax) {
            result.ageMax = 'Maximum age must not be below minimum age';
        }
        if (a.audienceAgeMin && a.audienceAgeMax && +a.audienceAgeMin > +a.audienceAgeMax) {
            result.audienceAgeMax = 'Maximum age must not be below minimum age';
        }
        if (Object.keys(draft.platforms).length === 0) result.platforms = 'Select at least one platform and placement';
        const mainWithoutPlacement = (selectedMain ?? []).filter(
            mainId => !platformItems.some(item => mainPlatformId(item) === mainId && item.id in draft.platforms)
        );
        if (mainWithoutPlacement.length) {
            result.platforms = `Select at least one placement for ${mainPlatforms
                .filter(main => mainWithoutPlacement.includes(main.id))
                .map(main => main.name)
                .join(', ')}`;
        }
        if (Object.values(draft.platforms).some(nbPosts => !(nbPosts >= 1))) {
            result.platforms = 'Number of posts must be at least 1';
        }
        if (draft.countries.length === 0) result.countries = 'Select at least one country';
        if (draft.categoryIds.length === 0) result.categories = 'Select at least one category';
        if (changes.audienceCountries && draft.audienceCountries.length === 0) {
            result.audienceCountries = 'Select at least one country';
        }
        return result;
    }, [
        draft,
        campaign,
        snapshot,
        selectedMain,
        platformItems,
        mainPlatforms,
        changes.audienceCountries,
        changes.timelineFlags,
    ]);

    const tabOfError = (key: string): Section => {
        if (key === 'platforms') return 'platforms';
        if (['ageMax', 'countries', 'categories'].includes(key)) return 'creators';
        if (['audienceAgeMax', 'audienceCountries'].includes(key)) return 'audience';
        return 'general';
    };

    function setValue(group: 'general' | 'creator' | 'audience', name: string, value: string) {
        setDraft(prev => (prev ? { ...prev, [group]: { ...prev[group], [name]: value } } : prev));
    }

    function toggleMainPlatform(mainId: number) {
        if ((selectedMain ?? []).includes(mainId)) {
            setSelectedMain(prev => (prev ?? []).filter(item => item !== mainId));
            setDraft(prev => {
                if (!prev) return prev;
                const platforms = { ...prev.platforms };
                platformItems
                    .filter(item => mainPlatformId(item) === mainId)
                    .forEach(item => delete platforms[item.id]);
                return { ...prev, platforms };
            });
        } else {
            setSelectedMain(prev => [...(prev ?? []), mainId]);
        }
    }

    function togglePlatform(platformId: number) {
        setDraft(prev => {
            if (!prev) return prev;
            const platforms = { ...prev.platforms };
            if (platformId in platforms) {
                delete platforms[platformId];
            } else {
                platforms[platformId] = 1;
            }
            return { ...prev, platforms };
        });
    }

    function setPlatformPosts(platformId: number, nbPosts: number) {
        setDraft(prev => (prev ? { ...prev, platforms: { ...prev.platforms, [platformId]: nbPosts } } : prev));
    }

    function toggleCategory(categoryId: number) {
        setDraft(prev =>
            prev
                ? {
                      ...prev,
                      categoryIds: prev.categoryIds.includes(categoryId)
                          ? prev.categoryIds.filter(item => item !== categoryId)
                          : [...prev.categoryIds, categoryId],
                  }
                : prev
        );
    }

    function toggleAllCategories() {
        setDraft(prev =>
            prev
                ? { ...prev, categoryIds: prev.categoryIds.length === 0 ? categories.map(category => category.id) : [] }
                : prev
        );
    }

    function save() {
        if (!draft) return;
        setShowErrors(true);
        const firstError = Object.keys(errors)[0];
        if (firstError) {
            setActiveTab(tabOfError(firstError));
            WarningToast('Please fix the highlighted fields');
            return;
        }
        setIsSaving(true);
        // Every section has its own endpoint, so only the changed ones are sent, one after another.
        const initialGeneral = JSON.parse(snapshot ?? '{}').general ?? {};
        const changedGeneral: Values = Object.fromEntries(
            Object.entries(draft.general).filter(([key, value]) => initialGeneral[key] !== value)
        );
        const scalarPayload = {
            ...(changes.general ? asPayload(changedGeneral, GENERAL_DATE_KEYS, ['reviewDays']) : {}),
            ...(changes.timelineFlags
                ? { isReviewed: draft.isReviewed, canSkipReporting: draft.canSkipReporting }
                : {}),
            ...(changes.barter ? { isCharity: draft.isBarter } : {}),
            ...(changes.creatorValues ? asPayload(draft.creator, [], ['ageMin', 'ageMax']) : {}),
            ...(changes.audienceValues ? asPayload(draft.audience, [], ['audienceAgeMin', 'audienceAgeMax']) : {}),
        };
        let chain: Promise<unknown> = Promise.resolve();
        if (Object.keys(scalarPayload).length) {
            chain = chain.then(() => CampaignsService.editCampaignAdvanced(id, scalarPayload));
        }
        if (changes.platforms) {
            chain = chain.then(() =>
                CampaignsService.saveAdvancedPlatforms(
                    id,
                    Object.entries(draft.platforms).map(([socialPlatformId, nbPosts]) => ({
                        socialPlatformId: +socialPlatformId,
                        nbPosts: +nbPosts,
                    }))
                )
            );
        }
        if (changes.countries) {
            chain = chain.then(() => CampaignsService.saveAdvancedCountries(id, draft.countries));
        }
        if (changes.categories) {
            chain = chain.then(() => CampaignsService.saveAdvancedCategories(id, draft.categoryIds));
        }
        if (changes.audienceCountries) {
            chain = chain.then(() => CampaignsService.saveAdvancedAudienceCountries(id, draft.audienceCountries));
        }
        if (changes.creatorType && draft.creatorTypeId) {
            chain = chain.then(() => CampaignsService.saveAdvancedCreatorType(id, +draft.creatorTypeId));
        }
        if (changes.goal && draft.goalId) {
            chain = chain.then(() => CampaignsService.saveAdvancedGoal(id, +draft.goalId));
        }
        chain
            .then(() => {
                toast(UPDATED_CAMPAIGN);
                navigate(-1);
            })
            .catch((err: unknown) => {
                ErrorToast(err);
                // Sections saved before the failure are already persisted, so reload the real state.
                load();
            })
            .finally(() => setIsSaving(false));
    }

    function cancel() {
        if (isDirty && !window.confirm('You have unsaved changes. Leave without saving?')) return;
        navigate(-1);
    }

    const tabTitle = (title: string, hasChanges: boolean) => (
        <span className="d-inline-flex align-items-center">
            {title}
            {hasChanges && (
                <span
                    className="d-inline-block rounded-circle bg-warning ms-2"
                    style={{ width: 8, height: 8 }}
                    title="Unsaved changes"
                />
            )}
        </span>
    );

    const fieldError = (key: string) =>
        showErrors && errors[key] ? <div className="text-danger fs-7 mt-1">{errors[key]}</div> : null;

    const countryOptions = (country ?? []).map((code: string) => ({
        value: code,
        label: getCountryName(code) || code,
        image: `/flags/4x3/${code.toLowerCase()}.svg`,
    }));
    const genderOptions = (gender ?? [])
        .filter((item: string) => item !== 'undefined')
        .map((item: string) => ({ value: item, label: getCorrectEnums(item) }));

    const textInput = (
        group: 'general' | 'creator' | 'audience',
        name: string,
        label: string,
        md: number,
        multiline = false
    ) => (
        <Form.Group as={Col} md={md} key={name}>
            <Form.Label>{label}</Form.Label>
            <Form.Control
                {...(multiline ? { as: 'textarea' as const, rows: 4 } : {})}
                value={draft?.[group][name] ?? ''}
                isInvalid={showErrors && !!errors[name]}
                onChange={e => setValue(group, name, e.target.value)}
            />
            {fieldError(name)}
        </Form.Group>
    );

    const typedInput = (
        group: 'general' | 'creator' | 'audience',
        type: 'date' | 'number',
        [name, label]: Field,
        md: number
    ) => (
        <Form.Group as={Col} md={md} key={name}>
            <Form.Label>{label}</Form.Label>
            <Form.Control
                type={type}
                min={type === 'number' ? 0 : undefined}
                value={draft?.[group][name] ?? ''}
                isInvalid={showErrors && !!errors[name]}
                onChange={e => setValue(group, name, e.target.value)}
            />
            {fieldError(name)}
        </Form.Group>
    );

    const genderSelect = (group: 'creator' | 'audience', name: string, label: string) => (
        <Form.Group as={Col} md={6} key={name}>
            <Form.Label>{label}</Form.Label>
            <SingleSelectDropdown
                placeholder="Select gender"
                options={genderOptions}
                selected={draft?.[group][name] ?? ''}
                onChange={value => setValue(group, name, value)}
            />
        </Form.Group>
    );

    function changeDuration(value: string) {
        setDraft(prev => {
            if (!prev) return prev;
            const invoicePeriod =
                value !== 'month_12' && prev.general.invoicePeriod === 'quarterly'
                    ? 'monthly'
                    : prev.general.invoicePeriod;
            return { ...prev, general: { ...prev.general, invoiceDuration: value, invoicePeriod } };
        });
    }

    function setReviewed(isReviewed: boolean) {
        setDraft(prev =>
            prev
                ? {
                      ...prev,
                      isReviewed,
                      general: { ...prev.general, reviewDays: isReviewed ? prev.general.reviewDays : '0' },
                  }
                : prev
        );
    }

    const longTermEnd = (() => {
        const months = Number(draft?.general.invoiceDuration.replace('month_', ''));
        if (!draft?.general.date || !months) return '';
        const end = new Date(draft.general.date);
        end.setDate(end.getDate() + months * LONG_TERM_DAYS_PER_MONTH);
        return end.toISOString().slice(0, 10);
    })();

    // The client is the brand company of the shared client users, like in the campaigns list.
    const clients: ICampaignAdvancedClient[] = (campaign as any)?.clients ?? [];
    const clientNames = Array.from(
        new Set(
            clients
                .map(client => client.brandCompany?.displayName || client.displayName)
                .filter((name): name is string => !!name)
        )
    );
    const coverImage = campaign?.campaignImages?.[0]?.thumbnail || campaign?.campaignImages?.[0]?.path;

    // Same chips as the campaigns list.
    const campaignType = campaign?.isLongTerm
        ? { label: 'Long', className: 'long' }
        : campaign?.parent?.id
        ? { label: 'Linked', className: 'linked' }
        : { label: 'Standard', className: 'standard' };

    if (!draft) return null;

    return (
        <div>
            <Card className="mb-3">
                <Card.Body className="d-flex flex-wrap align-items-center justify-content-between gap-4">
                    <div className="d-flex align-items-center gap-4">
                        <img
                            src={coverImage || '/images/placeholder.png'}
                            onError={event => {
                                event.currentTarget.onerror = null;
                                event.currentTarget.src = '/images/placeholder.png';
                            }}
                            alt={campaign?.title || 'Campaign'}
                            width={144}
                            height={72}
                            className="rounded flex-shrink-0"
                            style={{ objectFit: 'cover' }}
                        />
                        <div>
                            <h3 className="m-0">{campaign?.title}</h3>
                            <div className="text-muted fs-7 mt-1">ID {id}</div>
                            <div className="d-flex flex-wrap gap-2 mt-2">
                                <span className={`status ${campaignType.className}`}>{campaignType.label}</span>
                                {campaign?.isCompleted ? (
                                    <span className="status completed">completed</span>
                                ) : (
                                    campaign?.status && (
                                        <span className={`status ${campaign.status}`}>
                                            {campaign.status === 'published' ? 'public' : campaign.status}
                                        </span>
                                    )
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="d-flex flex-wrap gap-5">
                        {campaign?.company?.displayName && (
                            <div>
                                <div className="text-muted fs-8 text-uppercase">Company</div>
                                <div className="fw-bold">{campaign.company.displayName}</div>
                            </div>
                        )}
                        {clientNames.length > 0 && (
                            <div>
                                <div className="text-muted fs-8 text-uppercase">Client</div>
                                {clientNames.map(name => (
                                    <div key={name} className="fw-bold">
                                        {name}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </Card.Body>
            </Card>

            <Card>
                <Card.Body>
                    <Tabs
                        className="tabs-primary-border"
                        activeKey={activeTab}
                        onSelect={key => setActiveTab((key as Section) ?? 'general')}
                    >
                        <Tab eventKey="general" title={tabTitle('General', changedSections.general)}>
                            <div className="pt-4">
                                <Row className="g-3">
                                    {GENERAL_TEXT.map(([name, label, multiline]) =>
                                        textInput('general', name, label, 12, multiline)
                                    )}
                                    <Form.Group as={Col} md={6}>
                                        <Form.Label>Goal</Form.Label>
                                        <SingleSelectDropdown
                                            placeholder="Select goal"
                                            options={goals.map(goal => ({
                                                value: String(goal.id),
                                                label: goal.title,
                                            }))}
                                            selected={draft.goalId}
                                            onChange={value =>
                                                setDraft(prev => (prev ? { ...prev, goalId: value } : prev))
                                            }
                                        />
                                    </Form.Group>
                                    <Form.Group as={Col} md={6}>
                                        <Form.Label>Content creator type</Form.Label>
                                        <SingleSelectDropdown
                                            placeholder="Select creator type"
                                            options={creatorTypes.map(type => ({
                                                value: String(type.id),
                                                label: type.title,
                                            }))}
                                            selected={draft.creatorTypeId}
                                            onChange={value =>
                                                setDraft(prev => (prev ? { ...prev, creatorTypeId: value } : prev))
                                            }
                                        />
                                    </Form.Group>
                                    <Col md={12}>
                                        <h6 className="mt-3 mb-0">Timeline</h6>
                                    </Col>
                                    {campaign?.isLongTerm ? (
                                        <>
                                            {typedInput('general', 'date', ['date', 'Start date'], 4)}
                                            <Form.Group as={Col} md={4}>
                                                <Form.Label>Campaign duration</Form.Label>
                                                <SingleSelectDropdown
                                                    placeholder="Select duration"
                                                    options={DURATIONS}
                                                    selected={draft.general.invoiceDuration}
                                                    clearable={false}
                                                    onChange={changeDuration}
                                                />
                                                {fieldError('invoiceDuration')}
                                            </Form.Group>
                                            <Form.Group as={Col} md={4}>
                                                <Form.Label>Payout method</Form.Label>
                                                <SingleSelectDropdown
                                                    placeholder="Select payout method"
                                                    options={PERIODS.filter(
                                                        period =>
                                                            period !== 'quarterly' ||
                                                            draft.general.invoiceDuration === 'month_12'
                                                    ).map(period => ({
                                                        value: period,
                                                        label: getCorrectEnums(period),
                                                    }))}
                                                    selected={draft.general.invoicePeriod}
                                                    clearable={false}
                                                    onChange={value => setValue('general', 'invoicePeriod', value)}
                                                />
                                                {fieldError('invoicePeriod')}
                                            </Form.Group>
                                            {longTermEnd && (
                                                <Col md={12} className="text-muted fs-7">
                                                    Campaign runs until {longTermEnd}
                                                </Col>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            {typedInput('general', 'date', ['date', 'Application deadline'], 4)}
                                            {typedInput('general', 'date', ['publishFrom', 'Publication from'], 4)}
                                            {typedInput('general', 'date', ['publishTo', 'Publication to'], 4)}
                                            <Col md={12} className="d-flex align-items-start gap-3">
                                                <ToggleChip
                                                    label="Content approval"
                                                    checked={draft.isReviewed}
                                                    onChange={e => setReviewed(e.target.checked)}
                                                />
                                                {draft.isReviewed && (
                                                    <div style={{ width: 220 }}>
                                                        <Form.Control
                                                            type="number"
                                                            min={1}
                                                            placeholder="Days before scheduled posting"
                                                            value={draft.general.reviewDays}
                                                            isInvalid={showErrors && !!errors.reviewDays}
                                                            onChange={e =>
                                                                setValue('general', 'reviewDays', e.target.value)
                                                            }
                                                        />
                                                        {fieldError('reviewDays')}
                                                    </div>
                                                )}
                                            </Col>
                                            {typedInput(
                                                'general',
                                                'date',
                                                ['postingDeadline', 'Reporting deadline'],
                                                4
                                            )}
                                            <Col md={12}>
                                                <ToggleChip
                                                    label="Can skip reporting"
                                                    checked={draft.canSkipReporting}
                                                    onChange={e =>
                                                        setDraft(prev =>
                                                            prev
                                                                ? { ...prev, canSkipReporting: e.target.checked }
                                                                : prev
                                                        )
                                                    }
                                                />
                                            </Col>
                                        </>
                                    )}
                                    <Col md={12}>
                                        <ToggleChip
                                            label={draft.isBarter ? 'Barter deal' : 'Paid'}
                                            checked={draft.isBarter}
                                            onChange={e =>
                                                setDraft(prev =>
                                                    prev ? { ...prev, isBarter: e.target.checked } : prev
                                                )
                                            }
                                        />
                                    </Col>
                                </Row>
                            </div>
                        </Tab>

                        <Tab eventKey="platforms" title={tabTitle('Platforms', changedSections.platforms)}>
                            <div className="pt-4">
                                <Row className="g-4">
                                    {mainPlatforms.map(main => {
                                        const isMainSelected = (selectedMain ?? []).includes(main.id);
                                        return (
                                            <Col key={main.id} md={4}>
                                                <button
                                                    type="button"
                                                    onClick={() => toggleMainPlatform(main.id)}
                                                    className={`d-flex align-items-center gap-3 w-100 rounded border px-4 py-3 mb-3 bg-white fs-5 fw-bold ${
                                                        isMainSelected
                                                            ? 'border-primary bg-primary-subtle'
                                                            : 'border-secondary-subtle'
                                                    }`}
                                                >
                                                    <img
                                                        src={main.path || '/images/placeholder.png'}
                                                        alt=""
                                                        width={36}
                                                        height={36}
                                                    />
                                                    {getCorrectEnums(main.name)}
                                                </button>
                                                <div
                                                    className="d-flex flex-column gap-2"
                                                    style={{ opacity: isMainSelected ? 1 : 0.5 }}
                                                >
                                                    {platformItems
                                                        .filter(item => mainPlatformId(item) === main.id)
                                                        .map(item => {
                                                            const checked = item.id in draft.platforms;
                                                            return (
                                                                <div
                                                                    key={item.id}
                                                                    className="d-flex align-items-center gap-3"
                                                                >
                                                                    <button
                                                                        type="button"
                                                                        disabled={!isMainSelected}
                                                                        onClick={() => togglePlatform(item.id)}
                                                                        className={`d-inline-flex align-items-center gap-2 rounded-pill border px-3 py-2 bg-white fs-7 ${
                                                                            checked
                                                                                ? 'border-primary bg-primary-subtle'
                                                                                : 'border-secondary-subtle'
                                                                        }`}
                                                                        style={{ minWidth: 150 }}
                                                                    >
                                                                        <img
                                                                            src={item.path || '/images/placeholder.png'}
                                                                            alt=""
                                                                            width={24}
                                                                            height={24}
                                                                        />
                                                                        {getPlacementLabel(item.name)}
                                                                    </button>
                                                                    {checked && (
                                                                        <Form.Control
                                                                            type="number"
                                                                            min={1}
                                                                            style={{ width: 100 }}
                                                                            value={draft.platforms[item.id]}
                                                                            onChange={e =>
                                                                                setPlatformPosts(
                                                                                    item.id,
                                                                                    +e.target.value
                                                                                )
                                                                            }
                                                                        />
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                </div>
                                            </Col>
                                        );
                                    })}
                                </Row>
                                {fieldError('platforms')}
                            </div>
                        </Tab>

                        <Tab eventKey="creators" title={tabTitle('Content creators', changedSections.creators)}>
                            <div className="pt-4">
                                <Row className="g-3">
                                    {genderSelect('creator', 'gender', 'Influencer gender')}
                                    <Form.Group as={Col} md={6}>
                                        <Form.Label>Influencer location</Form.Label>
                                        <MultiSelectDropdown
                                            placeholder="Select countries"
                                            options={countryOptions}
                                            selected={draft.countries}
                                            onChange={selected =>
                                                setDraft(prev => (prev ? { ...prev, countries: selected } : prev))
                                            }
                                        />
                                        {fieldError('countries')}
                                    </Form.Group>
                                    {CREATOR_NUMBERS.map(field => typedInput('creator', 'number', field, 3))}
                                </Row>

                                <div className="d-flex justify-content-between align-items-center mt-4 mb-3">
                                    <h6 className="mb-0">Category</h6>
                                    <Button variant="outline-primary" size="sm" onClick={toggleAllCategories}>
                                        {draft.categoryIds.length === 0 ? 'Select all' : 'Deselect all'}
                                    </Button>
                                </div>
                                <div className="d-flex flex-wrap gap-2">
                                    {categories.map(category => {
                                        const isSelected = draft.categoryIds.includes(category.id);
                                        return (
                                            <button
                                                type="button"
                                                key={category.id}
                                                onClick={() => toggleCategory(category.id)}
                                                className={`d-inline-flex align-items-center gap-2 rounded-pill border px-3 py-2 bg-white fs-7 ${
                                                    isSelected
                                                        ? 'border-primary bg-primary-subtle'
                                                        : 'border-secondary-subtle'
                                                }`}
                                            >
                                                <img src={`/icons/categories/${category.name}.svg`} alt="" width={20} />
                                                {getCorrectEnums(category.name)}
                                            </button>
                                        );
                                    })}
                                </div>
                                {fieldError('categories')}
                            </div>
                        </Tab>

                        <Tab eventKey="audience" title={tabTitle('Audience', changedSections.audience)}>
                            <div className="pt-4">
                                <Row className="g-3">
                                    {textInput('audience', 'audienceDesc', 'Target audience description', 12, true)}
                                    {genderSelect('audience', 'audienceGender', 'Audience gender preference')}
                                    <Form.Group as={Col} md={6}>
                                        <Form.Label>Audience location</Form.Label>
                                        <MultiSelectDropdown
                                            placeholder="Select countries"
                                            options={countryOptions}
                                            selected={draft.audienceCountries}
                                            onChange={selected =>
                                                setDraft(prev =>
                                                    prev ? { ...prev, audienceCountries: selected } : prev
                                                )
                                            }
                                        />
                                        {fieldError('audienceCountries')}
                                    </Form.Group>
                                    {AUDIENCE_NUMBERS.map(field => typedInput('audience', 'number', field, 3))}
                                </Row>
                            </div>
                        </Tab>
                    </Tabs>
                </Card.Body>
            </Card>

            <div
                className="position-sticky bottom-0 bg-white border-top py-3 px-4 mt-4 mb-5 d-flex align-items-center justify-content-between rounded shadow-sm"
                style={{ zIndex: 10 }}
            >
                <span className={`fs-7 ${isDirty ? 'text-warning' : 'text-muted'}`}>
                    {isDirty ? '● Unsaved changes' : 'No changes'}
                </span>
                <div className="d-flex gap-2">
                    <Button variant="outline-primary" onClick={cancel} disabled={isSaving}>
                        Cancel
                    </Button>
                    <Button onClick={save} disabled={isSaving || !isDirty}>
                        {isSaving ? 'Saving...' : 'Save'}
                    </Button>
                </div>
            </div>
        </div>
    );
}
